import React, { useEffect, useState } from 'react';
import { praxisService } from '../../services/praxis.service';
import { Praxis, AppType } from '../../types';
import WeeklyScheduleReadOnly from '../zuweisung/WeeklyScheduleReadOnly';
import { useEnvironment } from '../../context/EnvironmentContext';

interface PraxisListProps {
  onEdit: (praxis: Praxis) => void;
  onDelete: (praxis: Praxis) => void;
  onCreate: () => void;
  refreshTrigger?: number;
}

const PraxisList: React.FC<PraxisListProps> = ({ onEdit, onDelete, onCreate, refreshTrigger }) => {
  const { currentApp } = useEnvironment();
  const [praxen, setPraxen] = useState<Praxis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPraxisForSchedule, setSelectedPraxisForSchedule] = useState<Praxis | null>(null);

  const fetchPraxen = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await praxisService.getAllPraxen(currentApp as AppType);
      setPraxen(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Praxen');
    } finally {
      setLoading(false);
    }
  }, [currentApp]);

  useEffect(() => {
    fetchPraxen();
  }, [refreshTrigger, fetchPraxen]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (selectedPraxisForSchedule) {
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">
            Zuweisungen für {selectedPraxisForSchedule.name}
          </h2>
          <button
            onClick={() => setSelectedPraxisForSchedule(null)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            ← Zurück zur Liste
          </button>
        </div>
        <WeeklyScheduleReadOnly praxisId={selectedPraxisForSchedule.id} refreshTrigger={refreshTrigger} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-semibold text-gray-800">Praxen Liste</h2>
        <button
          onClick={onCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
        >
          Neue Praxis
        </button>
      </div>

      {praxen.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Keine Praxen vorhanden. Erstellen Sie eine neue Praxis.
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stadt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telefon
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {praxen.map((praxis) => (
                <tr key={praxis.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {praxis.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {praxis.stadt || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {praxis.telefon || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {praxis.email || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => setSelectedPraxisForSchedule(praxis)}
                      className="text-green-600 hover:text-green-900 mr-4"
                    >
                      📅 Zuweisungen
                    </button>
                    <button
                      onClick={() => onEdit(praxis)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => onDelete(praxis)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PraxisList;
